import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllowedUsers, addUser, deleteUser, deleteUsers, updateUserTenant, updateUserRole, fetchTenants, resetUserPasswordByAdmin } from '../redux/actions';
import LogoSection from '../components/LogoSection';

const Users = () => {
    const dispatch = useDispatch();
    const logoURL = useSelector((state) => state.logoURL);
    const allowedUsers = useSelector((state) => state.allowedUsers);
    const tenants = useSelector((state) => state.tenants);
    const tenantID = useSelector((state) => state.userTenant);
    const userRole = useSelector((state) => state.userRole);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserTenant, setNewUserTenant] = useState(tenantID || '');
    const [selectedTenantFilter, setSelectedTenantFilter] = useState(tenantID);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [deleteDialog, setDeleteDialog] = useState({ show: false, userEmail: null, multiple: false });
    const [editingTenant, setEditingTenant] = useState(null);
    const [passwordResetDialog, setPasswordResetDialog] = useState({
        show: false,
        userEmail: null,
        newPassword: '',
        loading: false,
        success: false,
        error: null,
        message: null
    });
    const [newUserRole, setNewUserRole] = useState('user');

    const isAdmin = userRole === 'admin';
    const isTenantAdmin = userRole === 'tenant-admin';
    const canManageUsers = isAdmin || isTenantAdmin;
    const canAssignTenantAdmin = isAdmin || isTenantAdmin;
    const canAssignAdmin = isAdmin;

    useEffect(() => {
        dispatch(fetchAllowedUsers());
        if (canManageUsers) {
            dispatch(fetchTenants());
        }
    }, [dispatch, canManageUsers]);

    useEffect(() => {
        setSelectedTenantFilter(tenantID);
        // Set default tenant for new user form
        if (!isAdmin && tenantID) {
            setNewUserTenant(tenantID);
        }
    }, [tenantID, isAdmin]);

    const handleAddUser = async () => {
        if (!newUserEmail.trim()) {
            alert('Email is required');
            return;
        }

        if (!newUserTenant) {
            alert('Tenant is required');
            return;
        }

        // Validate role assignment permissions
        if (newUserRole === 'admin' && !canAssignAdmin) {
            alert('You do not have permission to create admin users');
            return;
        }

        const success = await dispatch(addUser(newUserEmail, newUserTenant, newUserRole));
        if (success) {
            setNewUserEmail('');
            setNewUserTenant(tenantID || '');
            setNewUserRole('user');
            setShowAddForm(false);
        }
    };

    const handleSelectUser = (userEmail) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userEmail)) {
                newSet.delete(userEmail);
            } else {
                newSet.add(userEmail);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const filteredUsers = getFilteredUsers();
        if (selectedUsers.size === filteredUsers.length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(filteredUsers.map(u => u.email)));
        }
    };

    const handleDeleteClick = (userEmail = null) => {
        if (userEmail) {
            setDeleteDialog({ show: true, userEmail, multiple: false });
        } else if (selectedUsers.size > 0) {
            setDeleteDialog({ show: true, userEmail: null, multiple: true });
        }
    };

    const handleDeleteConfirm = async () => {
        if (deleteDialog.multiple) {
            const emails = Array.from(selectedUsers);
            await dispatch(deleteUsers(emails));
            setSelectedUsers(new Set());
        } else {
            await dispatch(deleteUser(deleteDialog.userEmail));
        }
        setDeleteDialog({ show: false, userEmail: null, multiple: false });
    };

    const handleTenantChange = async (userEmail, newTenantId) => {
        await dispatch(updateUserTenant(userEmail, newTenantId));
        setEditingTenant(null);
    };

    const handleMakeAdmin = async (userEmail) => {
        if (window.confirm(`Are you sure you want to make ${userEmail} an admin?`)) {
            const success = await dispatch(updateUserRole(userEmail, 'admin'));
            if (success) {
                alert('User has been promoted to admin');
            } else {
                alert('Failed to update user role');
            }
        }
    };

    const handleMakeTenantAdmin = async (userEmail) => {
        if (window.confirm(`Are you sure you want to make ${userEmail} a tenant admin?`)) {
            const success = await dispatch(updateUserRole(userEmail, 'tenant-admin'));
            if (success) {
                alert('User has been promoted to tenant admin');
            } else {
                alert('Failed to update user role');
            }
        }
    };

    const handleDemoteToUser = async (userEmail) => {
        if (window.confirm(`Are you sure you want to demote ${userEmail} to regular user?`)) {
            const success = await dispatch(updateUserRole(userEmail, 'user'));
            if (success) {
                alert('User has been demoted to regular user');
            } else {
                alert('Failed to update user role');
            }
        }
    };

    const handleResetPassword = (userEmail) => {
        setPasswordResetDialog({
            show: true,
            userEmail: userEmail,
            newPassword: '',
            loading: false,
            success: false,
            error: null,
            message: null
        });
    };

    const handlePasswordResetSubmit = async (e) => {
        e.preventDefault();
        const { userEmail, newPassword } = passwordResetDialog;

        if (!newPassword || newPassword.length < 6) {
            setPasswordResetDialog(prev => ({
                ...prev,
                error: 'Password must be at least 6 characters long'
            }));
            return;
        }

        setPasswordResetDialog(prev => ({
            ...prev,
            loading: true,
            error: null
        }));

        const result = await dispatch(resetUserPasswordByAdmin(userEmail, newPassword));

        if (result.success) {
            setPasswordResetDialog(prev => ({
                ...prev,
                loading: false,
                success: true,
                message: result.message || 'Password has been reset successfully!',
                error: null
            }));
        } else {
            setPasswordResetDialog(prev => ({
                ...prev,
                loading: false,
                success: false,
                error: result.error || 'Failed to reset password',
                message: null
            }));
        }
    };

    const handleClosePasswordDialog = () => {
        setPasswordResetDialog({
            show: false,
            userEmail: null,
            newPassword: '',
            loading: false,
            success: false,
            error: null,
            message: null
        });
    };

    const getFilteredUsers = () => {
        if (isAdmin && selectedTenantFilter) {
            return allowedUsers.filter((user) => user.tenantID === selectedTenantFilter);
        } else if (isAdmin && !selectedTenantFilter) {
            return allowedUsers;
        } else {
            // Tenant-admins and regular users only see users from their own tenant
            return allowedUsers.filter((user) => user.tenantID === tenantID);
        }
    };

    const getTenantName = (tenantId) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant ? tenant.tenantName : tenantId || 'No Tenant';
    };

    const filteredUsers = getFilteredUsers();
    const allSelected = filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length;

    return (
        <div className="min-h-screen flex justify-center">
            <div className="w-full max-w-screen-lg flex flex-col items-center space-y-3 px-4 md:px-16">
                <LogoSection />

                <div className="w-full">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-3xl text-center">Users</h1>
                        <button
                            onClick={() => {
                                setNewUserTenant(tenantID || '');
                                setShowAddForm(true);
                            }}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                            + Add New User
                        </button>
                    </div>

                    {/* Tenant Filter (Admin Only) */}
                    {isAdmin && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filter by Tenant
                            </label>
                            <select
                                value={selectedTenantFilter || ''}
                                onChange={(e) => setSelectedTenantFilter(e.target.value || null)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Tenants</option>
                                {tenants.map((tenant) => (
                                    <option key={tenant.id} value={tenant.id}>
                                        {tenant.tenantName || tenant.id}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Bulk Delete Button (Admin and Tenant-Admin) */}
                    {canManageUsers && selectedUsers.size > 0 && (
                        <div className="mb-4">
                            <button
                                onClick={() => handleDeleteClick()}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete Selected ({selectedUsers.size})
                            </button>
                        </div>
                    )}

                    {showAddForm && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={newUserEmail}
                                    onChange={(e) => setNewUserEmail(e.target.value)}
                                    placeholder="Enter user email"
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tenant {isAdmin ? '(Required)' : ''}
                                </label>
                                <select
                                    value={newUserTenant}
                                    onChange={(e) => setNewUserTenant(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    disabled={!isAdmin}
                                >
                                    <option value="">Select Tenant</option>
                                    {tenants.map((tenant) => (
                                        <option key={tenant.id} value={tenant.id}>
                                            {tenant.tenantName || tenant.id}
                                        </option>
                                    ))}
                                </select>
                                {!isAdmin && (
                                    <p className="text-sm text-gray-500 mt-1">
                                        Defaulting to your tenant: {getTenantName(tenantID)}
                                    </p>
                                )}
                            </div>
                            {canManageUsers && (
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Role
                                    </label>
                                    <select
                                        value={newUserRole}
                                        onChange={(e) => setNewUserRole(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="user">User</option>
                                        <option value="tenant-admin">Tenant Admin</option>
                                        {canAssignAdmin && <option value="admin">Admin</option>}
                                    </select>
                                </div>
                            )}
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleAddUser}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                    Add User
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewUserEmail('');
                                        setNewUserTenant(tenantID || '');
                                        setNewUserRole('user');
                                    }}
                                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead>
                                <tr className="bg-gray-100">
                                    {canManageUsers && (
                                        <th className="py-2 px-4 border-b">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={handleSelectAll}
                                                className="cursor-pointer"
                                            />
                                        </th>
                                    )}
                                    <th className="py-2 px-4 border-b">Email</th>
                                    <th className="py-2 px-4 border-b">Role</th>
                                    {isAdmin && (
                                        <th className="py-2 px-4 border-b">Tenant</th>
                                    )}
                                    <th className="py-2 px-4 border-b">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        {canManageUsers && (
                                            <td className="py-2 px-4 border-b text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.has(user.email)}
                                                    onChange={() => handleSelectUser(user.email)}
                                                    className="cursor-pointer"
                                                />
                                            </td>
                                        )}
                                        <td className="py-2 px-4 border-b text-center">{user.email}</td>
                                        <td className="py-2 px-4 border-b text-center">{user.role}</td>
                                        {isAdmin && (
                                            <td className="py-2 px-4 border-b text-center">
                                                {editingTenant === user.email ? (
                                                    <select
                                                        value={user.tenantID || ''}
                                                        onChange={(e) => handleTenantChange(user.email, e.target.value)}
                                                        onBlur={() => setEditingTenant(null)}
                                                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                                                        autoFocus
                                                    >
                                                        <option value="">No Tenant</option>
                                                        {tenants.map((tenant) => (
                                                            <option key={tenant.id} value={tenant.id}>
                                                                {tenant.tenantName || tenant.id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span
                                                        onClick={() => setEditingTenant(user.email)}
                                                        className="cursor-pointer hover:text-blue-600 underline"
                                                        title="Click to change tenant"
                                                    >
                                                        {getTenantName(user.tenantID)}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td className="py-2 px-4 border-b text-center">
                                            <div className="flex justify-center items-center gap-2 flex-wrap">
                                                {/* Make Tenant-Admin button - visible to admin and tenant-admin */}
                                                {canAssignTenantAdmin && user.role === 'user' && (
                                                    <button
                                                        onClick={() => handleMakeTenantAdmin(user.email)}
                                                        className="px-3 py-1 text-sm rounded bg-green-500 text-white hover:bg-green-600"
                                                        title="Make Tenant Admin"
                                                    >
                                                        Make Tenant Admin
                                                    </button>
                                                )}
                                                {/* Make Admin button - admin only */}
                                                {canAssignAdmin && user.role !== 'admin' && (
                                                    <button
                                                        onClick={() => handleMakeAdmin(user.email)}
                                                        className="px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
                                                        title="Make Admin"
                                                    >
                                                        Make Admin
                                                    </button>
                                                )}
                                                {/* Demote to User button - for tenant-admins (admin and tenant-admin can demote) */}
                                                {canAssignTenantAdmin && user.role === 'tenant-admin' && (
                                                    <button
                                                        onClick={() => handleDemoteToUser(user.email)}
                                                        className="px-3 py-1 text-sm rounded bg-yellow-500 text-white hover:bg-yellow-600"
                                                        title="Demote to User"
                                                    >
                                                        Demote to User
                                                    </button>
                                                )}
                                                {/* Demote Admin - admin only can demote other admins */}
                                                {canAssignAdmin && user.role === 'admin' && (
                                                    <button
                                                        onClick={() => handleDemoteToUser(user.email)}
                                                        className="px-3 py-1 text-sm rounded bg-yellow-500 text-white hover:bg-yellow-600"
                                                        title="Demote to User"
                                                    >
                                                        Demote to User
                                                    </button>
                                                )}
                                                {/* Reset Password - admin only */}
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => handleResetPassword(user.email)}
                                                        className="px-3 py-1 text-sm rounded bg-purple-500 text-white hover:bg-purple-600"
                                                        title="Reset Password"
                                                    >
                                                        Reset Password
                                                    </button>
                                                )}
                                                {/* Delete button - visible to admin and tenant-admin */}
                                                {canManageUsers && (
                                                    <button
                                                        onClick={() => handleDeleteClick(user.email)}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Delete user"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {deleteDialog.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                        <p className="mb-4">
                            {deleteDialog.multiple
                                ? `Are you sure you want to delete ${selectedUsers.size} user(s)? This action cannot be undone.`
                                : `Are you sure you want to delete user "${deleteDialog.userEmail}"? This action cannot be undone.`}
                        </p>
                        <div className="flex space-x-4 justify-end">
                            <button
                                onClick={() => setDeleteDialog({ show: false, userEmail: null, multiple: false })}
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Reset Dialog */}
            {passwordResetDialog.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        {!passwordResetDialog.success ? (
                            <>
                                <h2 className="text-xl font-bold mb-4">Reset Password</h2>
                                <p className="mb-4 text-gray-600">
                                    Enter a new password for <strong>{passwordResetDialog.userEmail}</strong>
                                </p>
                                <form onSubmit={handlePasswordResetSubmit}>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            value={passwordResetDialog.newPassword}
                                            onChange={(e) => setPasswordResetDialog(prev => ({
                                                ...prev,
                                                newPassword: e.target.value,
                                                error: null
                                            }))}
                                            placeholder="Enter new password (min 6 characters)"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            required
                                            minLength={6}
                                            disabled={passwordResetDialog.loading}
                                        />
                                        {passwordResetDialog.error && (
                                            <p className="mt-2 text-sm text-red-600">{passwordResetDialog.error}</p>
                                        )}
                                    </div>
                                    <div className="flex space-x-4 justify-end">
                                        <button
                                            type="button"
                                            onClick={handleClosePasswordDialog}
                                            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                            disabled={passwordResetDialog.loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                                            disabled={passwordResetDialog.loading || !passwordResetDialog.newPassword}
                                        >
                                            {passwordResetDialog.loading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Resetting...
                                                </>
                                            ) : (
                                                'Reset Password'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <>
                                <h2 className="text-xl font-bold mb-4 text-green-600">Success!</h2>
                                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-green-800 font-medium">{passwordResetDialog.message}</p>
                                    <p className="mt-2 text-sm text-gray-600">
                                        The password for <strong>{passwordResetDialog.userEmail}</strong> has been reset successfully.
                                    </p>
                                </div>
                                <div className="flex space-x-4 justify-end">
                                    <button
                                        onClick={handleClosePasswordDialog}
                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
